import React, { useMemo } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
  type Chart,
  type LegendItem,
  type Plugin,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { dashboardTheme } from "../theme/dashboardTheme";
import { IFinancingInsight } from "../types/ownerDashboard.types";
import {
  FINANCING_GAP_COLOR,
  formatFinancingTimelineLabel,
  getMonthlyGap,
  resolveFinancingMilestoneChartIndex,
} from "../utils/buildFinancingProjectionTimeline";
import { mapApiProjectionTimeline } from "../utils/mapFinancingProjectionTimeline";

const FINANCING_MILESTONE_LINE_PLUGIN_ID = "financingMilestoneLine";
const MILESTONE_MARKER_LABEL = "Marco zero";

function createFinancingMilestoneLinePlugin(milestoneIndex: number): Plugin<"line"> {
  return {
    id: FINANCING_MILESTONE_LINE_PLUGIN_ID,
    afterDatasetsDraw(chart) {
      if (milestoneIndex < 0) {
        return;
      }

      const milestoneDatasetIndex = chart.data.datasets.findIndex(
        (dataset) => dataset.label === MILESTONE_MARKER_LABEL
      );
      const milestoneMeta =
        milestoneDatasetIndex >= 0 ? chart.getDatasetMeta(milestoneDatasetIndex) : null;
      const pointElement = milestoneMeta?.data[milestoneIndex] ?? chart.getDatasetMeta(0).data[milestoneIndex];

      if (!pointElement) {
        return;
      }

      const { ctx, chartArea } = chart;

      ctx.save();
      ctx.strokeStyle = dashboardTheme.colors.positive;
      ctx.globalAlpha = 0.65;
      ctx.lineWidth = 1.5;
      ctx.setLineDash([4, 4]);
      ctx.beginPath();
      ctx.moveTo(pointElement.x, chartArea.top);
      ctx.lineTo(pointElement.x, chartArea.bottom);
      ctx.stroke();
      ctx.restore();
    },
  };
}

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler
);

interface FinancingMilestoneChartProps {
  insight: IFinancingInsight;
  ipca12mAccumulatedPct: number;
}

function getDatasetBorderDash(dataset: object): number[] {
  if (!("borderDash" in dataset) || !Array.isArray(dataset.borderDash)) {
    return [];
  }

  return dataset.borderDash.filter((value): value is number => typeof value === "number");
}

function buildFinancingChartLegendItems(chart: Chart<"line">): LegendItem[] {
  return chart.data.datasets.flatMap((dataset, datasetIndex) => {
    if (dataset.label === "Diferença mensal" || dataset.label === MILESTONE_MARKER_LABEL) {
      return [];
    }

    const item: LegendItem = {
      text: String(dataset.label ?? ""),
      fillStyle: "transparent",
      strokeStyle: String(dataset.borderColor ?? dashboardTheme.colors.textSecondary),
      fontColor: dashboardTheme.colors.textSecondary,
      lineWidth: 2,
      lineDash: getDatasetBorderDash(dataset),
      hidden: !chart.isDatasetVisible(datasetIndex),
      index: datasetIndex,
      datasetIndex,
    };

    return [item];
  });
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

export default function FinancingMilestoneChart({
  insight,
  ipca12mAccumulatedPct,
}: FinancingMilestoneChartProps) {
  const { timeline, milestoneMonth } = useMemo(
    () =>
      mapApiProjectionTimeline(
        insight.projection_timeline,
        insight,
        ipca12mAccumulatedPct
      ),
    [insight, ipca12mAccumulatedPct]
  );

  const milestoneIndex = useMemo(
    () => resolveFinancingMilestoneChartIndex(timeline, milestoneMonth),
    [timeline, milestoneMonth]
  );

  const chartPlugins = useMemo(
    () => [createFinancingMilestoneLinePlugin(milestoneIndex)],
    [milestoneIndex]
  );

  const rentSeriesLabel =
    insight.projection_basis === "current_rent"
      ? "Aluguel líquido médio"
      : "Aluguel projetado (IPCA)";

  const chartData = useMemo(() => {
    const labels = timeline.map((point) => formatFinancingTimelineLabel(point.month));
    const milestonePoint = milestoneIndex >= 0 ? timeline[milestoneIndex] : null;

    return {
      labels,
      datasets: [
        {
          label: rentSeriesLabel,
          data: timeline.map((point) => point.projectedRent),
          borderColor: dashboardTheme.colors.chartLine,
          backgroundColor: "rgba(110, 168, 255, 0.15)",
          fill: true,
          tension: 0.35,
          pointRadius: 0,
          pointHoverRadius: 4,
          borderWidth: 2,
          order: 2,
        },
        {
          label: "Parcela do financiamento",
          data: timeline.map((point) => point.installment),
          borderColor: "#c9d1d9",
          backgroundColor: "transparent",
          borderDash: [6, 4],
          pointRadius: 0,
          pointHoverRadius: 0,
          fill: false,
          tension: 0,
          borderWidth: 2,
          order: 1,
        },
        {
          label: MILESTONE_MARKER_LABEL,
          data: timeline.map((_, index) =>
            index === milestoneIndex && milestonePoint ? milestonePoint.installment : null
          ),
          borderColor: dashboardTheme.colors.positive,
          backgroundColor: dashboardTheme.colors.positive,
          pointRadius: timeline.map((_, index) => (index === milestoneIndex ? 6 : 0)),
          pointHoverRadius: timeline.map((_, index) => (index === milestoneIndex ? 7 : 0)),
          pointBorderColor: dashboardTheme.colors.positive,
          pointBorderWidth: 2,
          borderWidth: 0,
          fill: false,
          showLine: false,
          order: 0,
        },
        {
          label: "Diferença mensal",
          data: timeline.map((point) => {
            const gap = getMonthlyGap(point.projectedRent, point.installment);
            return gap > 0 ? gap : null;
          }),
          borderColor: FINANCING_GAP_COLOR,
          backgroundColor: FINANCING_GAP_COLOR,
          pointRadius: 0,
          pointHoverRadius: 0,
          borderWidth: 0,
          fill: false,
          showLine: false,
        },
      ],
    };
  }, [timeline, milestoneIndex, rentSeriesLabel]);

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: {
          left: 12,
          right: 8,
          top: 8,
        },
      },
      interaction: {
        mode: "index" as const,
        intersect: false,
      },
      plugins: {
        legend: {
          position: "bottom" as const,
          labels: {
            color: dashboardTheme.colors.textSecondary,
            boxWidth: 28,
            boxHeight: 0,
            padding: 16,
            font: { size: 11 },
            generateLabels: buildFinancingChartLegendItems,
          },
        },
        tooltip: {
          backgroundColor: dashboardTheme.colors.surfaceElevated,
          borderColor: dashboardTheme.colors.cardBorder,
          borderWidth: 1,
          titleColor: dashboardTheme.colors.textPrimary,
          bodyColor: dashboardTheme.colors.textSecondary,
          callbacks: {
            title: (items: { dataIndex: number }[]) => {
              const point = timeline[items[0]?.dataIndex ?? 0];
              if (!point) {
                return "";
              }

              if (point.month === 0) {
                return "Hoje";
              }

              if (point.month % 12 === 0) {
                const years = point.month / 12;
                return years === 1 ? "Daqui a 1 ano" : `Daqui a ${years} anos`;
              }

              return `Daqui a ${point.month} meses`;
            },
            label: (context: { dataset: { label?: string }; parsed: { y: number | null } }) => {
              if (context.dataset.label === MILESTONE_MARKER_LABEL) {
                return "Marco zero: aluguel cobre a parcela";
              }

              return `${context.dataset.label}: ${formatCurrency(context.parsed.y ?? 0)}`;
            },
            labelTextColor: (context: { dataset: { label?: string } }) => {
              if (context.dataset.label === "Diferença mensal") {
                return FINANCING_GAP_COLOR;
              }

              if (context.dataset.label === MILESTONE_MARKER_LABEL) {
                return dashboardTheme.colors.positive;
              }

              return dashboardTheme.colors.textSecondary;
            },
            filter: (item: { dataset: { label?: string }; parsed: { y: number | null } }) => {
              if (item.dataset.label === MILESTONE_MARKER_LABEL) {
                return milestoneIndex >= 0;
              }

              return item.parsed.y !== null;
            },
          },
        },
      },
      scales: {
        x: {
          grid: { color: dashboardTheme.colors.chartGrid },
          ticks: {
            color: dashboardTheme.colors.chartTick,
            maxRotation: 0,
            autoSkip: true,
            maxTicksLimit: 8,
            font: { size: 10 },
          },
        },
        y: {
          grace: "8%",
          grid: { color: dashboardTheme.colors.chartGrid },
          ticks: {
            color: dashboardTheme.colors.chartTick,
            callback: (value: string | number) => formatCurrency(Number(value)),
            font: { size: 10 },
          },
        },
      },
    }),
    [timeline, milestoneIndex]
  );

  return (
    <div className="flex min-w-0 flex-col gap-2">
      <p className="text-[0.65rem] font-semibold uppercase tracking-wide text-[#6e7681]">
        Projeção de aluguel e parcela
      </p>
      <div className="h-36 w-full min-w-0 max-w-full sm:h-44">
        <Line data={chartData} options={options} plugins={chartPlugins} />
      </div>
      {milestoneMonth !== null && (
        <p className="text-xs text-[#8b949e]">
          {milestoneMonth === 0
            ? "A linha e o ponto verde marcam hoje: o aluguel já cobre a parcela."
            : "A linha e o ponto verde marcam o cruzamento entre aluguel projetado e parcela."}
        </p>
      )}
      {insight.covers_installment && (
        <p className="text-xs text-[#8b949e]">
          {insight.amortization_system === "sac"
            ? "Projeção futura com base no IPCA dos últimos 12 meses, com parcela decrescente (SAC)."
            : "Projeção futura com base no IPCA dos últimos 12 meses, mantendo a parcela fixa como referência."}
        </p>
      )}
    </div>
  );
}
